import { Component, HostListener, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { IonRange } from "@ionic/angular";
import { GenericBasicGUI } from "src/model/class/component classes/generic-basic-gui";
import { GaugeShape } from "src/model/enum/enumCustomInterface";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { GaugeCircularConfiguration } from "src/model/interface/gauge-circular-configuration";
import { GaugeLinearConfiguration } from "src/model/interface/gauge-linear-configuration";

@Component({
    selector: "app-gauge",
    templateUrl: "./gauge.component.html",
    styleUrls: ["./gauge.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GaugeComponent extends GenericBasicGUI implements OnInit {
  _logContext: string = "Gauge.Component";

  public gaugeCircularConfiguration: GaugeCircularConfiguration = null;
  public gaugeLinearConfiguration: GaugeLinearConfiguration = null;

  public gaugeValue: number = 0;
  public gaugeCallbackTextFormat: any = null;

  public isLinear: boolean = false; // linear gauge in normal view

  public linearStep: number = 1;

  public styles = {
    "font-family": "Arial",
    color: "#000000",
    "font-size": "15px",
    "font-style": "normal", // italic
    "font-weight": "normal", // bol
    "font-decorator": "none", // underline
    left: "0px",
    top: "0px",
    width: "10px",
    height: "10px",
    position: "absolute",
    "align-items": "center",
    "justify-content": "center",
    display: "flex",
  };

  public chartStyle = {
    width: "100%",
    height: "100%",
    top: "0px",
    left: "0px",
  };

  public stylesChild = {};

  //public handleSeek;

  @ViewChild("linearGaugeRef", { static: false }) linearGaugeRef: IonRange;

  /**
   * Giorgia -
   *
   * @param injector The injector param is used by the parent classes to inject the services that they needed:
   * using this trick if the parent classes needed some specific service I don't need to inject them here
   * but I can do the injection in the specific class.
   */
  constructor(injector: Injector) {
    super(injector);
  }

  /**
   * With this gauge (ngx-gauge@v1.0.0-beta.13) it's possible to have this properties:
   * size type min max value cap thick label foregroundColor backgroundColor append prepend duration
   * thresholds animate aria-label aria-labelledby
   * https://www.npmjs.com/package/ngx-gauge
   */
  ngOnInit() {

    /*
    this.loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "ngOnInit()",
      "START"
    );
    */

    this.subscribeSettingServiceData();
    this.isLinear = this.isLinearGauge();

    [this.styles, this.stylesChild] = super.HandleProperties(
      this.styles,
      this.stylesChild
    );
    this.styles["backgroundColor"] = "transparent";

    if (this.isLinear) {
      this.gaugeLinearConfiguration = this.getLinearSliderConfiguration();
      this.calculateGaugeLinearStep();
      this.setLinearGaugeStyle();
    } else {
      this.gaugeCircularConfiguration = this.getCircularSliderConfiguration();
    }

  }

  ngOnDestroy() {
    this.onDestroyChildren();
  }


  /**
 * Mouse up event handler on desktop/web
 */
  public MouseUp(): void {

    //this.loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "MouseUp()", "OK!!");
    if (!this.platform.is("ios") && !this.platform.is("android")) {
      this.onDragKnobEnd();
    }
  }

  /**
   * Mouse up event handler on device
   */
  public TouchEnd(): void {
    this.onDragKnobEnd();
  }

  /**
   * Handle change range event from native ion-range component (linear gauge)
   */
  private async onDragKnobEnd(): Promise<void> {
    await this.hanleRequestConfirmAndSendValue(
      this.linearGaugeRef.value.toString()
    );
  }

  /**
   * Callback output event received from circular gauge event when press plus or minus button
   * for increment or decrement value.
   * It's write over the bus.
   *
   * @param newValueToWrite New value to write
   */
  public async onRequestWriteValue(newValueToWrite: number): Promise<void> {
    await this.hanleRequestConfirmAndSendValue(newValueToWrite.toString());
  }

  /**
   * Calculate linear gauge single step
   *
   * @returns Effective step value
   */
  private calculateGaugeLinearStep() {
    const res = (
      (this.gaugeLinearConfiguration.maxValue -
        this.gaugeLinearConfiguration.minValue) /
      this.gaugeLinearConfiguration.majorTick
    );

    this.loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "calculateGaugeLinearStep()",
      "Linear step: " + res
    );

    //return res;
    this.linearStep = res;
  }

  /**
   * Handle data change for gauge component
   */
  protected DataChangeGauge(): void {
    this.gaugeValue = parseFloat(this.stateAddress.lastValue);
  }


  /**
   * Handle request password or confirm popup request (if needed) and perform send command.
   */
  private async hanleRequestConfirmAndSendValue(
    valueToSend: string
  ): Promise<void> {
    const canExecuteCommand: boolean =
      await this.handleOpenPopupRequestConfirmCommandExecution();

    if (canExecuteCommand) {
      const isPasswordOk: boolean = await this.isCommandPasswordOk();

      if (isPasswordOk) {
        if (
          parseFloat(valueToSend) !== parseFloat(this.stateAddress.lastValue)
        ) {
          this.SendCommand(valueToSend.toString(), 1, false);
        }
      } else {
        this.restoreGuagePreviosValue(this.stateAddress.lastValue);
      }
    } else {
      this.restoreGuagePreviosValue(this.stateAddress.lastValue);
    }
  }

  /**
   * Check if component is linear gauge
   */
  private isLinearGauge(): boolean {
    return (
      parseInt(this.ComponentAPI.properties["GaugeShape"]) == GaugeShape.LINEAR
    );
  }

  /**
   * Restore linear gauge previos value
   *
   * @param valueToRestore Value to restore over the gauge
   */
  private restoreGuagePreviosValue(valueToRestore: string): void {
    this.gaugeValue = Number(this.stateAddress.lastValue); //Restore linear gauge value with prev value
  }

  /**
   * Get linear slider configuration
   */
  private getLinearSliderConfiguration(): GaugeLinearConfiguration {

    return {
      minValue: Number(this.ComponentAPI.properties["MinValue"]),
      maxValue: Number(this.ComponentAPI.properties["MaxValue"]),
      showTicks: this.ComponentAPI.properties["ShowTicks"]?.toUpperCase() === "TRUE",
      majorTick: Number(this.ComponentAPI.properties["MajorTickCount"]),
      isKnobDraggable: this.canValueSliderBeChanged(),
    };
  }

  /**
   * Adding supervision list addional style for linear gauge
   */
  private setLinearGaugeStyle(): void {
    if (!this.ShowingInDesktopVersion) {
      this.styles["width"] = "100%";
      this.styles["height"] = "auto";
    }
  }

  /**
   * Return circular gauge base configuration object by generic component API
   *
   * @returns
   */
  private getCircularSliderConfiguration(): GaugeCircularConfiguration {
    const { width, height } = this.getCircularSliderSize();

    /* Fallback with minimum value to assegnate to "minorTick" property */
    let minorTickVar: number = Number(this.ComponentAPI.properties["MinorTickCount"]);
    if (minorTickVar <= 0) {
      minorTickVar = 1
    }


    return {
      width: width,
      height: height,
      minValue: Number(this.ComponentAPI.properties["MinValue"]),
      maxValue: Number(this.ComponentAPI.properties["MaxValue"]),
      majorTick: Number(this.ComponentAPI.properties["MajorTickCount"]),
      minorTick: minorTickVar,
      showLabel:
        this.ComponentAPI.properties["ShowLabels"]?.toUpperCase() === "TRUE"
      ,
      lineSize: this.lineSize || 15,

      //ATTENTION!!! "|| true" is a TEMPORARY fallback, as for now property "ShowLabelValue" IS NOT PASSED BY SERVER INSIDE "ComponentAPI.properties"
      showCurrentValueLabel:
        this.ComponentAPI.properties["ShowLabelValue"]
          ?.toString()
          .toUpperCase() == "TRUE" || true,
      //,
      showPointer:
        this.ComponentAPI.properties["ShowNeedle"]?.toString().toUpperCase() ==
        "TRUE"
      ,
      colorRange: this.ComponentAPI.shapeLayout,
      showButtonCommand: this.canValueSliderBeChanged(),
    };
  }

  /**
   * Return circular slider size object (with width and height property)
   */
  private getCircularSliderSize(): any {
    let width: string = this.styles["width"];
    let height: string = this.styles["height"];

    if (!this.ShowingInDesktopVersion) {
      width = "100%";
      height = "300px";
    }

    return {
      width,
      height,
    };
  }

  /**
   * handle if value slider can be changed (both circular and linear)
   *
   * @returns button slider show
   */
  private canValueSliderBeChanged(): boolean {
    return this.isClickActionSetValue(this.clickAction) && !this.readOnly;
  }
}
